import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { invoiceTypes } from "@/constants/invoiceTypes";
import { useToast } from "@/hooks/use-toast";
import { useDownloadTemplate, useImport } from "@/hooks/useImport";
import { useMappingProfiles } from "@/hooks/useMappingProfiles";
import { UploadFormData, uploadSchema } from "@/schema/upload.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { CheckCircle, CircleAlert, Loader2, Upload } from "lucide-react";
import { Controller, useForm } from "react-hook-form";

const ImportPage = () => {
  const { toast } = useToast();
  const { uploadFile, isUploading } = useImport();
  const { downloadTemplate, isDownloading } = useDownloadTemplate();
  const dismissNotice = () => {
    setShowNotice(false);
  };

  const [showNotice, setShowNotice] = useState(true);
  const [fileInputKey, setFileInputKey] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UploadFormData>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      typeCode: "01",
      strictValidation: true,
      file: new DataTransfer().files,
    },
  });

  const typeCode = watch("typeCode");
  const mappingProfileId = watch("mappingProfileId");
  const isSelfBilled = ["11", "12", "13", "14"].includes(typeCode);

  const { profiles } = useMappingProfiles();
  const selectedProfile = profiles.find((p) => p.id === mappingProfileId);

  // When a mapping profile is selected, lock the invoice type to the profile's first
  // applicable type code
  useEffect(() => {
    if (!selectedProfile) return;
    const lockedType = selectedProfile.applicableTypeCodes?.[0];
    if (lockedType) {
      setValue("typeCode", lockedType as UploadFormData["typeCode"]);
    }
  }, [selectedProfile, setValue]);

  const onSubmit = async (data: UploadFormData) => {
    await uploadFile(data, {
      onSuccess: (response) => {
        const {
          invoicesCount,
          totalCount,
          rejectedInvoices,
          failedInvoices,
          posRefNotFoundInvoices,
          noUuidInvoices,
        } = response.data.data;
        const rejected: Array<{ posReferenceNumber: string; row: number }> =
          rejectedInvoices ?? [];
        const failed: Array<{ posReferenceNumber: string; errors: any[] }> =
          failedInvoices ?? [];

        const notFound: Array<{ posReferenceNumber: string; row: number }> =
          posRefNotFoundInvoices ?? [];
        const noUuid: Array<{ posReferenceNumber: string }> =
          noUuidInvoices ?? [];

        const hasSuccess = invoicesCount > 0;
        const hasNotFound = notFound.length > 0;

        if (hasSuccess && !hasNotFound) {
          // All CN/DN/RN lookups succeeded (or file had no CN/DN/RN) — pure success
          const attempted = totalCount ?? invoicesCount;
          const description =
            attempted > invoicesCount
              ? `${invoicesCount} of ${attempted} invoice(s) imported successfully.`
              : `${invoicesCount} invoice(s) have been imported successfully.`;
          toast({
            title: "File Uploaded Successfully",
            description,
            duration: 5000,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
          });
        } else if (hasSuccess && hasNotFound) {
          // Partial: some imported, some skipped because original invoice not found
          const refList = notFound
            .map((r) => `Row ${r.row} (${r.posReferenceNumber})`)
            .join(", ");
          toast({
            title: `${invoicesCount} imported, ${notFound.length} failed — original invoice not found`,
            description: `Imported ${invoicesCount} invoice(s). Could not find original invoice for: ${refList}`,
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: Infinity,
          });
        } else if (!hasSuccess && hasNotFound) {
          // Nothing imported — all failed original invoice lookup
          const refList = notFound
            .map((r) => `Row ${r.row} (${r.posReferenceNumber})`)
            .join(", ");
          toast({
            title: `${notFound.length} CN/DN/RN invoice(s) not imported — original invoice not found`,
            description: `No matching invoice in QuickBill for: ${refList}`,
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: Infinity,
          });
        }

        if (failed.length > 0) {
          // Build per-field detail lines
          const detailLines: string[] = [];
          failed.forEach((f) => {
            const errs = f.errors ?? [];
            if (errs.length === 0) {
              detailLines.push(`• [${f.posReferenceNumber}] failed validation`);
              return;
            }
            errs.forEach((err: any) => {
              const tab =
                err.tab && err.tab !== "Unknown" ? `${err.tab} · ` : "";
              const where = err.column || err.field || "field";
              const rowPart = err.row ? ` (row ${err.row})` : "";
              const valuePart =
                err.value !== null &&
                err.value !== undefined &&
                err.value !== ""
                  ? ` — got "${err.value}"`
                  : " — value is empty/missing";
              detailLines.push(
                `• [${f.posReferenceNumber}] ${tab}${where}${rowPart}: ${err.message}${valuePart}`,
              );
            });
          });

          // Full structured detail for power users / debugging.
          console.error("Import validation failures:", failed);

          const shown = detailLines.slice(0, 8).join("\n");
          const more =
            detailLines.length > 8
              ? `\n…and ${detailLines.length - 8} more (open the browser console for the full list)`
              : "";

          toast({
            title: `${failed.length} invoice(s) failed validation — not imported`,
            description: `${shown}${more}`,
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: Infinity,
          });
        }

        if (rejected.length > 0) {
          const refList = rejected
            .map((r) => `Row ${r.row} (POS Ref: ${r.posReferenceNumber})`)
            .join(", ");
          toast({
            title: `${rejected.length} invoice(s) skipped — duplicate POS reference`,
            description: `Already exists in QuickBill: ${refList}`,
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: Infinity,
          });
        }

        if (noUuid.length > 0) {
          const refList = noUuid.map((r) => r.posReferenceNumber).join(", ");
          toast({
            title: `${noUuid.length} invoice(s) imported without LHDN UUID`,
            description: `Original invoice not yet validated by LHDN. Submission will fail until it is validated. Once validated, UUID will be auto-allocated. POS Ref: ${refList}`,
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: Infinity,
          });
        }

        if (
          invoicesCount === 0 &&
          rejected.length === 0 &&
          failed.length === 0 &&
          notFound.length === 0
        ) {
          toast({
            title: "Nothing to import",
            description: "No invoices were found in the uploaded file.",
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: 4000,
          });
        }

        reset();
      },
      onError: (error) => {
        let errorMessage = "Something went wrong. Please try again later.";
        const errorDetails: string[] = [];

        if (error instanceof AxiosError) {
          const responseData = error.response?.data;
          errorMessage = responseData?.message || errorMessage;

          console.error("Upload validation errors:", responseData);

          if (
            error.response?.status === 400 &&
            responseData?.data?.validationResults
          ) {
            const validationResults = responseData.data.validationResults;
            const failedInvoices = validationResults.filter(
              (r: any) => r.errors && r.errors.length > 0,
            );
            const failedCount = failedInvoices.length;

            failedInvoices.forEach((vr: any, idx: number) => {
              if (vr.errors && vr.errors.length > 0) {
                const invoiceCode =
                  vr.errors[0]?.invoiceCode || `Invoice ${idx + 1}`;

                const errorsByTab: Record<string, any[]> = {};
                vr.errors.forEach((err: any) => {
                  const tab = err.tab || "Unknown";
                  if (!errorsByTab[tab]) {
                    errorsByTab[tab] = [];
                  }
                  errorsByTab[tab].push(err);
                });

                Object.entries(errorsByTab).forEach(([tab, errors]) => {
                  errors.forEach((err: any) => {
                    let errorLine = `• [${invoiceCode}] ${tab}`;

                    if (err.row) {
                      errorLine += ` Row ${err.row}`;
                    }

                    errorLine += ` - ${err.column}: ${err.message}`;

                    if (
                      err.value !== null &&
                      err.value !== undefined &&
                      err.value !== ""
                    ) {
                      errorLine += ` (value: "${err.value}")`;
                    }

                    errorDetails.push(errorLine);
                  });
                });
              }
            });

            errorMessage = `${failedCount} of ${responseData.data.invoicesCount} invoice(s) failed validation:\n\n${errorDetails.slice(0, 5).join("\n")}`;

            if (errorDetails.length > 5) {
              errorMessage += `\n\n...and ${errorDetails.length - 5} more error(s). Check console for full details.`;
            }
          } else if (
            responseData?.details &&
            Array.isArray(responseData.details)
          ) {
            const detailMessages = responseData.details
              .map((d: any) => `• ${d.path}: ${d.message}`)
              .join("\n");
            errorMessage = `${errorMessage}\n\n${detailMessages}`;

            console.error("Validation details:", responseData.details);
          }
        }
        // Reset the file input so the user must re-select the file
        setValue("file", new DataTransfer().files);
        setFileInputKey((k) => k + 1);

        toast({
          title: "Upload Failed",
          description: errorMessage,
          variant: "destructive",
          icon: <CircleAlert size={30} className="animate-spin-once" />,
          duration: errorDetails && errorDetails.length > 0 ? Infinity : 8000,
        });
      },
    });
  };

  const handleDownloadTemplate = (templateType: string) => {
    downloadTemplate(templateType);
  };

  return (
    <div className="h-full flex items-start justify-center px-4">
      <Dialog
        open={showNotice}
        onOpenChange={(open) => {
          if (!open) dismissNotice();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Updated Import Template & Logic</DialogTitle>
            <DialogDescription>
              Please read before uploading invoices
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 text-sm text-slate-700 dark:text-slate-300">
            <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-950/20 p-3 text-blue-800 dark:text-blue-300">
              <p className="font-semibold mb-1.5">New grouping logic</p>
              <p>
                Invoices are now grouped by{" "}
                <span className="font-semibold">POS Reference Number</span>{" "}
                across all sheets. Every sheet (buyer info, line items, amounts,
                etc.) must use the same POS Reference Number to be treated as
                one invoice. The old Buyer Code–based grouping is no longer
                used.
              </p>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/20 p-3 text-amber-800 dark:text-amber-300">
              <p className="font-semibold mb-1.5">New unified template</p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  A single template now covers both{" "}
                  <span className="font-semibold">Strict</span> and{" "}
                  <span className="font-semibold">Permissive</span> modes —
                  download it via the
                  <span className="font-semibold">
                    {" "}
                    1. Invoice Template
                  </span>{" "}
                  button above.
                </li>
                <li>
                  Column headers are colour-coded:{" "}
                  <span className="font-semibold text-red-600">red</span> =
                  required in both modes,{" "}
                  <span className="font-semibold text-orange-500">orange</span>{" "}
                  = required in Strict only,
                  <span className="font-semibold text-green-600"> green</span> =
                  optional in both modes.
                </li>
                <li>
                  The Instructions &amp; Legend sheet inside the template
                  explains every field in detail.
                </li>
              </ul>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-xs">
              Old templates are no longer compatible. If you encounter upload
              errors, please re-download the latest template.
            </p>
          </div>
          <Button onClick={dismissNotice} className="w-full mt-1">
            Got it
          </Button>
        </DialogContent>
      </Dialog>
      <div className="w-full space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Import Invoice</CardTitle>
            <CardDescription>Upload and store invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="w-full h-full flex flex-col gap-2 items-center">
              <div className="w-full flex flex-col gap-2.5 bg-orange-50 border border-orange-100 text-slate-500 dark:bg-orange-950/20 dark:border-orange-900/50 dark:text-slate-400 p-2.5 rounded-md select-none">
                <p className="text-sm font-bold">
                  Note:{" "}
                  <span className="font-normal">
                    Imported invoices will be issued to the current active
                    branch. Please confirm your current active branch before
                    start uploading.
                  </span>
                </p>
                <p className="text-sm font-bold">
                  Instructions:{" "}
                  <span className="font-normal">
                    Download the template available for each invoice types.
                    Uploading unsupported template may result a failed upload.
                  </span>
                </p>
              </div>
              <div className="w-full flex flex-col gap-2.5 items-start text-sm bg-gray-100 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                <div className="w-full flex flex-col sm:flex-row items-start sm:justify-evenly">
                  <Button
                    size="sm"
                    type="button"
                    variant="link"
                    disabled={isDownloading}
                    onClick={() => handleDownloadTemplate("invoice")}
                  >
                    1. Invoice Template
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant="link"
                    disabled={isDownloading}
                    onClick={() => handleDownloadTemplate("invoice_note")}
                  >
                    2. CN/DN/RN Template
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant="link"
                    disabled={isDownloading}
                    onClick={() =>
                      handleDownloadTemplate("self_billed_invoice")
                    }
                  >
                    3. Self-billed Invoice Template
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    variant="link"
                    disabled={isDownloading}
                    onClick={() =>
                      handleDownloadTemplate("self_billed_invoice_note")
                    }
                  >
                    4. Self-billed CN/DN/RN Template
                  </Button>
                </div>
              </div>
              <form
                className="w-full md:w-3/4 h-full flex flex-col p-8 gap-4 rounded-md mt-4 border"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div>
                  <Label htmlFor="typeCode" required>
                    1. Invoice type
                  </Label>
                  <Controller
                    name="typeCode"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isUploading || !!selectedProfile}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Select invoice type" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceTypes.map((type) => (
                            <SelectItem value={type.Code} key={type.Code}>
                              {type.Description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {selectedProfile && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Invoice type is locked by the selected mapping profile
                      &ldquo;{selectedProfile.name}&rdquo;.
                    </p>
                  )}
                  {errors.typeCode && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.typeCode?.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label>2. Validation Mode</Label>
                  <Controller
                    name="strictValidation"
                    control={control}
                    render={({ field }) => (
                      <div className="flex flex-col gap-3">
                        <div className="inline-flex w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 p-1 gap-1">
                          <button
                            type="button"
                            disabled={isUploading}
                            onClick={() => field.onChange(true)}
                            className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                              field.value
                                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-sm"
                                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                            }`}
                          >
                            Strict
                          </button>
                          <button
                            type="button"
                            disabled={isUploading}
                            onClick={() => field.onChange(false)}
                            className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                              !field.value
                                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-sm"
                                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                            }`}
                          >
                            Permissive
                          </button>
                        </div>
                        {field.value ? (
                          <div className="text-sm rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-950/20 p-3 text-blue-800 dark:text-blue-300">
                            <p className="font-semibold mb-1">Strict Mode</p>
                            <ul className="list-disc list-inside space-y-0.5 text-blue-700 dark:text-blue-400">
                              <li>
                                All fields are required — any missing value will
                                cause the upload to fail
                              </li>
                              <li>
                                {isSelfBilled
                                  ? "Supplier details, amounts, dates, and line items must all be complete"
                                  : "Buyer details, amounts, dates, and line items must all be complete"}
                              </li>
                              <li>
                                POS Reference Number is mandatory in every sheet
                              </li>
                              <li>
                                Use this when your data is complete and verified
                              </li>
                            </ul>
                          </div>
                        ) : (
                          <div className="text-sm rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/20 p-3 text-amber-800 dark:text-amber-300">
                            <p className="font-semibold mb-1">
                              Permissive Mode
                            </p>
                            {isSelfBilled ? (
                              <ul className="list-disc list-inside space-y-0.5 text-amber-700 dark:text-amber-400">
                                <li>
                                  Buyer is always your{" "}
                                  <strong>company profile</strong> — the invoice
                                  is issued to yourself on behalf of the
                                  supplier
                                </li>
                                <li>
                                  A supplier TIN provided in your file is always
                                  kept. Only a blank/NA TIN defaults to{" "}
                                  <strong>EI00000000030</strong> (Foreign
                                  Supplier)
                                </li>
                                <li>
                                  Missing supplier registration type/number
                                  defaults to{" "}
                                  <strong>BRN / 000000000000</strong>
                                </li>
                                <li>
                                  Missing supplier MSIC code defaults to{" "}
                                  <strong>00000</strong>
                                </li>
                                <li>
                                  Missing supplier address/city/state defaults
                                  to <strong>N/A</strong>
                                </li>
                                <li>
                                  Missing supplier email and contact default to{" "}
                                  <strong>NA</strong>
                                </li>
                                <li>
                                  Missing amounts default to <strong>0</strong>
                                </li>
                                <li>
                                  Missing classification defaults to{" "}
                                  <strong>Others (022)</strong>
                                </li>
                                <li>
                                  Missing tax type defaults to{" "}
                                  <strong>Not Applicable (06)</strong>
                                </li>
                              </ul>
                            ) : (
                              <ul className="list-disc list-inside space-y-0.5 text-amber-700 dark:text-amber-400">
                                <li>
                                  Missing buyer info defaults to{" "}
                                  <strong>General Public</strong> (TIN:
                                  EI00000000010)
                                </li>
                                <li>
                                  Missing amounts default to <strong>0</strong>
                                </li>
                                <li>
                                  Missing invoice date defaults to{" "}
                                  <strong>today</strong>
                                </li>
                                <li>
                                  Missing classification defaults to{" "}
                                  <strong>Others (022)</strong>
                                </li>
                                <li>
                                  Missing tax type defaults to{" "}
                                  <strong>Not Applicable (06)</strong>
                                </li>
                                <li>
                                  Buyer Code is optional — leave empty to assign
                                  to General Public
                                </li>
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
                {profiles.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <Label>3. Mapping Profile (optional)</Label>
                    <Controller
                      name="mappingProfileId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={(val) =>
                            field.onChange(val === "none" ? undefined : val)
                          }
                          value={field.value ?? "none"}
                          disabled={isUploading}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="No mapping profile (use QuickBill template directly)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              No mapping profile
                            </SelectItem>
                            {profiles
                              .filter((p) => p.isActive)
                              .map((p) => (
                                <SelectItem value={p.id} key={p.id}>
                                  {p.name}
                                  {p.vendorName ? ` — ${p.vendorName}` : ""} (
                                  {p.applicableTypeCodes?.join(", ")})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      Select a mapping profile to automatically convert a vendor
                      file into QuickBill format before importing. The invoice
                      type will be locked to the profile&apos;s configuration.
                    </p>
                  </div>
                )}
                <div>
                  <Label required htmlFor="file">
                    {profiles.length > 0 ? "4." : "3."} Choose file to upload
                    (max 5MB)
                  </Label>
                  <Input
                    key={fileInputKey}
                    id="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    type="file"
                    {...register("file")}
                    errors={!!errors.file}
                    disabled={isUploading}
                  />
                  {errors.file && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.file?.message}
                    </p>
                  )}
                </div>
                <Button type="submit" disabled={isUploading}>
                  {isUploading ? (
                    <>
                      <Loader2
                        size={10}
                        className="animate-spin text-primary"
                      />
                      Uploading
                    </>
                  ) : (
                    <>
                      <Upload /> Upload
                    </>
                  )}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImportPage;
